import React from 'react';
import styled from '@emotion/styled';
import { useLocale } from 'locale';
import { SectionTitle } from '../SectionText';
import { Accordion, AccordionItem } from './Accordion';
import { Link } from 'ds/components/link';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(5)};
  text-align: center;

  ${({ theme }) => theme.media.showAt({ from: 2 })`
    margin-bottom: ${theme.spacing(6)};
  `}
`;

const Content = styled.div`
  ${({ theme }) => theme.media.showAt({ from: 2 })`
    max-width: 776px;
    width: 100%;
  `}
`;

const TextContent = styled.div`
  > * {
    display: inline-block;
  };
`;

const items: AccordionItem[] = [
  {
    id: 'q1',
    faqId: 'what_is_madad',
    question: 'איך מדד המתווכים של מדלן עוזר לי למצוא משרד תיווך אמין?',
    answer:
      'קנייה או מכירה של נכס היא לרוב העסקה הפיננסית הכי גדולה שתעשו בחיים, ולכן בחירת איש המקצוע הנכון שילווה אתכם בתהליך היא קריטית. מדד המתווכים של מדלן הוקם כדי לעשות לכם סדר: הוא מדרג באופן אובייקטיבי את עשרת המשרדים המובילים (ה-TOP 10) בעשרות ערים ואזורים בארץ. במקום לנחש או להסתמך על שמועות, המדד מציג רק משרדים עם קבלות וניסיון מוכח בשטח, כדי שתוכלו לקבל החלטות נכונות בראש שקט.',
  },
  {
    id: 'q2',
    faqId: 'why_madad_reliable',
    question: 'למה מדד המתווכים של מדלן הוא הדירוג הכי רלוונטי בשוק?',
    answer:
      'בשוק שבו קל להבטיח הבטחות, המדד שלנו הוא כלי שקוף שמבוסס נטו על נתונים ועובדות. הוא מסנן מתוך מאות משרדים את אלו שבאמת עובדים ומייצרים תוצאות באזור שלכם. כך לא תבזבזו זמן יקר על מתווכים שאין להם היסטוריה מוכחת של הצלחות באזור המגורים הספציפי שלכם.',
  },
  {
    id: 'q3',
    faqId: 'how_calculated',
    question: 'איך אתם יודעים כמה עסקאות המשרד באמת עשה? (אמינות המדד)',
    answer:
      'אמינות היא הבסיס לדירוג שלנו. הנתונים במדד לא מבוססים על הצהרות בעל פה, אלא על מחקר מעמיק של צוות האנליסטים שלנו. אנחנו בוחנים את כמות העסקאות, היקפן הכספי ומספר הנכסים בבלעדיות, ומאמתים ומצליבים את הנתונים ישירות מול דיווחים רשמיים. משרד תיווך שנתפס באי-סדרים או בדיווח כוזב - נפסל מיידית מהמדד.',
  },
  {
    id: 'q4',
    faqId: 'top_10_meaning',
    question: 'מה ההבדל בין מדד המתווכים של מדלן לבין התואר "מומחה שכונה"?',
    answer:
      'בעוד שמדד המתווכים מדרג את משרדי התיווך המובילים והחזקים ביותר ברמת העיר או האזור כולו, התואר "מומחה שכונה" מוענק למתווכים ספציפיים שהוכיחו התמחות מיקרו-מקומית, היכרות עמוקה וכמות עסקאות גדולה בשכונה ספציפית.',
  },
  {
    id: 'q5',
    faqId: 'participation_criteria',
    question: 'למה כדאי לבדוק את מדד המתווכים של מדלן לפני שחותמים על הסכם בלעדיות?',
    answer:
      'חתימה על בלעדיות קושרת אתכם למשרד תיווך למספר חודשים. לפני שאתם מפקידים את הנכס שלכם בידיו, מדד המתווכים של מדלן מאפשר לכם לבדוק בחינם את כוח השיווק האמיתי של המשרד: איזה נתח מהעסקאות בעיר הוא סגר השנה? והאם הוא באמת מנהל נכסים רבים בבלעדיות? הנתונים האלו יבטיחו שאתם מפקידים את הדירה שלכם בידיים הנכונות.',
  },
  {
    id: 'q6',
    faqId: 'contact_more_info',
    question: 'אני מתווך או בעל משרד תיווך, איך אני יכול להצטרף אל מדד המתווכים של מדלן?',
    answer: (
      <TextContent>
        משרדי תיווך המעוניינים להופיע בדירוג נדרשים להגיש את מועמדותם מדי שנה, על בסיס הנתונים של המשרד. אם אתם משרד פעיל באתר, עליכם לוודא שכל העסקאות שביצעתם בשנה החולפת משויכות אליכם כראוי במערכת. לכניסה לאזור האישי ולשיוך עסקאות לחצו כאן. במידה והמשרד שלכם עדיין לא פעיל במדלן ואתם מעוניינים להצטרף ולקחת חלק, <Link href="https://www.madlan.co.il/lp/agents" target="_blank">היכנסו לקישור המצורף</Link>
      </TextContent>
    ),
  },
];

export const FaqSection: React.FC = () => {
  const { t } = useLocale();

  return (
    <Wrapper>
      <Title>
        <SectionTitle weight="bold">
          {t('madad.2026.faq.title')}
        </SectionTitle>
      </Title>
      <Content>
        <Accordion items={items} />
      </Content>
    </Wrapper>
  );
};
